
'use client'

import { useEffect, useRef } from 'react'
import posthog from 'posthog-js'
import { useAuth } from '@/modules/auth/contexts/AuthContext'

interface HsSearchTrackingProps {
  q?: string
  chapterIds?: string
  taxTypes?: string
  totalResults: number
}

interface HsDetailTrackingProps {
  hsCode: string
}

function getBasePayload(userId: string, pageName: string) {
  return {
    user_id: userId,
    page_name: pageName,
    page_url: window.location.href,
    page_path: window.location.pathname,
  }
}

function usePageScrollTracking(pageName: string, contextId?: string) {
  const { user } = useAuth()
  const trackedMilestones = useRef(new Set<number>())
  const keyRef = useRef(`${pageName}:${contextId}`)

  useEffect(() => {
    // Nếu chuyển trang hoặc thay đổi query, reset lại trạng thái track
    const newKey = `${pageName}:${contextId}`
    if (keyRef.current !== newKey) {
      trackedMilestones.current.clear()
      keyRef.current = newKey
    }

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = document.documentElement.clientHeight

      // Nếu nội dung màn hình đủ ngắn và không cần scroll, coi như đã xem 100%
      const scrollDepth = scrollHeight <= clientHeight
        ? 100
        : (scrollTop / (scrollHeight - clientHeight)) * 100

      const milestones = [50, 75, 100]

      milestones.forEach((milestone) => {
        if (Math.round(scrollDepth) >= milestone && !trackedMilestones.current.has(milestone)) {
          trackedMilestones.current.add(milestone)

          posthog.capture('page_scrolled', {
            ...getBasePayload(user?.email || 'anonymous', pageName),
            scroll_depth: milestone.toString(),
            context_id: contextId || null,
          })
        }
      })
    }

    // Check lần đầu phòng khi màn hình dài không cần cuộn
    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [pageName, contextId, user?.email])
}

export function HsHomePageTracking() {
  usePageScrollTracking('hs_home_page')
  return null
}

export function HsSearchTracking({
  q,
  chapterIds,
  taxTypes,
  totalResults,
}: HsSearchTrackingProps) {
  const { user } = useAuth()
  const sentKeyRef = useRef<string | null>(null)

  // Track Page Scroll
  usePageScrollTracking('hs_search_results', q || chapterIds || '')

  useEffect(() => {
    const userId = user?.email || 'anonymous'
    const key = `${userId}:${window.location.pathname}:${window.location.search}:${totalResults}`
    if (sentKeyRef.current === key) return
    sentKeyRef.current = key

    const basePayload = getBasePayload(userId, 'hs_search_results')
    const query = q || null
    const selectedChapterIds = chapterIds || null

    posthog.capture('hs_code_searched', {
      ...basePayload,
      query,
      chapterIds: selectedChapterIds,
      taxTypes: taxTypes || null,
      result_count: totalResults,
    })
  }, [chapterIds, q, taxTypes, totalResults, user?.email])

  return null
}

export function HsDetailTracking({ hsCode }: HsDetailTrackingProps) {
  const { user } = useAuth()
  const sentKeyRef = useRef<string | null>(null)

  // Track Page Scroll
  usePageScrollTracking('hs_detail', hsCode)

  useEffect(() => {
    const userId = user?.email || 'anonymous'
    const key = `${userId}:${window.location.pathname}:${window.location.search}:${hsCode}`
    if (sentKeyRef.current === key) return
    sentKeyRef.current = key

    posthog.capture('hs_detail_viewed', {
      ...getBasePayload(userId, 'hs_detail'),
      hs_id: hsCode,
      url_query: window.location.search || null,
      url_fragment: window.location.hash || null,
    })
  }, [])

  return null
}
